import { FactoryDefaultKeyBindings, IKeybinding } from "./Input";
import { BuiltInCubeMaps, IVector3, SortDirections } from "./Misc";
import { ProjectListOrderByAttributes, ProjectListSortingParameters } from "./API";
import { ProjectListFilters } from "./enums";
import { IAICompanionPrompt } from "./AI";
import { SelectionType, TimelineEffectorTypes, WorldUnitScales } from "../globals";

export interface IEditorPreferences
{
	/** whether the auto-save functionality is enabled */
	autoSaveEnabled: boolean;
	/** interval in minutes between save invocations when auto-save is enabled */
	autoSaveInterval: number;
	/** whether to show a confirmation dialog when setting the project state to "approved" */
	skipDialogSetApprovedState: boolean;
	/** whether to show a confirmation dialog when setting the project state to "request changes" */
	skipDialogSetRequestChangesState: boolean;
	/** whether to show a confirmation dialog when moving an asset in the Asset Library from Personal to Company assets */
	enableDialogMoveAsset: boolean;
	/** whether to show a confirmation dialog when deleting an asset from the Asset Library */
	enableDialogDeleteAsset: boolean;
	/** the page the welcome screen will load to (all projects, demo projects, etc.) */
	welcomeScreenOpeningPageView: ProjectListFilters;
	/** the filtering/sorting options of the project list in the welcome screen (they are saved per-page) */
	welcomeScreenLastSortDetails: Partial<Record<ProjectListFilters, ProjectListSortingParameters>>;
	/** the units used for displaying dimensions and positions of 3D objects in the scene */
	unitDisplay: WorldUnitScales;
	/** the size in meters (on the Z axis) of the media-plane object that is created when an image asset is "imported" to the 3D scene */
	imageImportHeight: number;
	/** the size in meters (on the Z axis) of the media-plane object that is created when an video asset is "imported" to the 3D scene */
	videoImportHeight: number;
	/** whether tooltips are displayed when the user hovers over html elements that define the `tooltip` or `data-tooltip` props/attributes */
	enableTooltips: boolean;
	/** a list of keyboard shortcuts for invokiong editor functions (start simulation, set gizmo type, etc.) */
	keybindings: Record<string, IKeybinding>;
	/** whether to display information about the current selection in the scene view window */
	boundsInformationEnabled: boolean;
	/** decimal digits to display for each of the selection bounds axis in the viewport */
	boundsInformationPrecision: number;
	/** when set to true, the default scene background color will be used */
	renderDefaultBackground: boolean;
	/** a css hex string color (e.g. `#ff88a4`) for a custom scene background color. will only be used when `renderDefaultBackground` is set to false */
	viewportBackgroundColor: string;
	/** when set to true, this switches the default direction of dollying when navigating the scene camera with the mouse (wheel or alt+RMB) */
	invertZoom: boolean;
	/** determines the object priority between children-first of parent-first when drag/marquee-selecting objects in the scene view */
	sceneViewSelectChildren: SelectionType;
	/** determines whether to select the parent or the child when first clicing on an object in the scene view to select it */
	sceneViewFirstClickSelectsParent: boolean;
	/** the length in seconds assigned to timelines when they are created (where the loop marker is placed) */
	defaultTimelineLength: number;
	/** the duration in seconds assigned to timebars when they are created */
	defaultTimebarLength: number;
	/** a css hex color string (e.g. `#ff88a4`) assigned to the `Highlight Color` value field of "Highlight Sphere" timebars when they are created */
	defaultHighlightSphereColor: string;
	/** the size in meters - scaled by `unitDisplay` - assigned to the `Diameter` value field of "Highlight Sphere" timebars when they are created */
	defaultHighlightSphereSize: number;
	/** a css hex color string (e.g. `#ff88a4`) assigned to the `Target Color` value field of "Set Object Color" timebars when they are created */
	defaultSetObjectColor: string;
	/** a map of css hex color strings used for the background color of timebars on the timeline (both new and existing) */
	timebarColors: Partial<Record<TimelineEffectorTypes, string>>;
	/** the value assigned to the `Scale` property of a callout when it's first added to an object */
	defaultCalloutScale: number;
	/** the value assigned to the `Offset` of a callout when it's first added to an object */
	defaultCalloutOffset: IVector3;
	/** used in the Sequence Editor to determine whether to show connections between Sequences */
	showSequenceConnections: boolean;
	/** used in the Sequence Editor to determine whether to show connections to Steps generated from (legacy) UI Headers */
	showHeadersConnections: boolean;
	/** used in the Sequence Editor to determine whether to show connections to Steps  generated from (legacy) UI Options */
	showOptionsConnections: boolean;
	/** used in the Sequence Editor to determine whether to show connections to Steps  generated from (legacy) UI Insets */
	showInsetsConnections: boolean;
	/** used in the Sequence Editor to determine whether to show connections to Steps  generated from (legacy) UI Buttons */
	showCancelConnections: boolean;
	/** used in the Sequence Editor to determine whether to show animated arrowheads illustrating the flow direction connections (from-to) */
	showConnectionsFlow: boolean;
	/** which cubemap will be used in projects when they are created */
	newProjectEnvironment: BuiltInCubeMaps;
	/** a list of system prompts used by the AI Companion */
	aiCompanionPrompts: Array<IAICompanionPrompt>;
	/** the index of the selected promp from `aiCompanionPrompts` array that will be used by the AI companion */
	aiCompanionSelectedPromptIndex: number;
}

export interface IProjectSettings 
{
	environmentMapId: BuiltInCubeMaps,
}

export enum PreferencesPages {
	general = "general",
	keybindings = "keybindings",
	sceneView = "sceneView",
	assetLibrary = "assetLibrary",
	sequenceEditor = "sequenceEditor",
	timeline = "timeline",
	objectProps = "objectProps",
	aiCompanion = "aiCompanion",
	newProjectSettings = "newProjectSettings",
	workspace = "workspace",
	environment = "environment",
}

export const defaultEditorPreferences: IEditorPreferences = {
	autoSaveEnabled: true,
	autoSaveInterval: 10,
	skipDialogSetApprovedState: false,
	skipDialogSetRequestChangesState: false,
	enableDialogMoveAsset: true,
	enableDialogDeleteAsset: true,
	welcomeScreenOpeningPageView: ProjectListFilters.all_projects,
	welcomeScreenLastSortDetails: {
		[ProjectListFilters.recent]: {
			attribute: ProjectListOrderByAttributes.UpdatedAt,
			direction: SortDirections.Descending,
		},
		[ProjectListFilters.all_projects]: {
			attribute: ProjectListOrderByAttributes.Name,
			direction: SortDirections.Ascending,
		},
		[ProjectListFilters.my_projects]: {
			attribute: ProjectListOrderByAttributes.Name,
			direction: SortDirections.Ascending,
		},
		[ProjectListFilters.shared_with_me]: {
			attribute: ProjectListOrderByAttributes.Author,
			direction: SortDirections.Ascending,
		},
	},
	unitDisplay: WorldUnitScales.Meters,
	imageImportHeight: 0.1,
	videoImportHeight: 0.1,
	enableTooltips: true,
	keybindings: { ...FactoryDefaultKeyBindings },
	boundsInformationEnabled: true,
	boundsInformationPrecision: 2,
	renderDefaultBackground: true,
	viewportBackgroundColor: "#bbbbbb",
	invertZoom: false,
	sceneViewSelectChildren: SelectionType.Child,
	sceneViewFirstClickSelectsParent: true,
	defaultTimelineLength: 10,
	defaultTimebarLength: 1,
	defaultHighlightSphereColor: "#00ffff",
	defaultHighlightSphereSize: 0.1,
	defaultSetObjectColor: "#ff00ff",
	timebarColors: {
		[TimelineEffectorTypes.EventInvocation]: "#D7AB7A",
		[TimelineEffectorTypes.ObjectColor]: "#CE5A75",
		[TimelineEffectorTypes.ObjectFade]: "#10CEA7",
		[TimelineEffectorTypes.HighlightSphere]: "#B558C9",
		[TimelineEffectorTypes.TransformTranslate]: "#CBCD8F",
		[TimelineEffectorTypes.TransformRotate]: "#ACB32F",
		[TimelineEffectorTypes.TransformRotatePivot]: "#ACB32F",
		[TimelineEffectorTypes.TransformScale]: "#CAA61A",
		[TimelineEffectorTypes.ObjectAnimation]: "#D02A49",
		[TimelineEffectorTypes.VideoPlayback]: "#537998",
		[TimelineEffectorTypes.AudioPlayback]: "#D6664B",				
	},
	defaultCalloutScale: 1,
	defaultCalloutOffset: { x: 0.3, y: 0.3, z: 1 },
	showSequenceConnections: true,
	showHeadersConnections: false,
	showOptionsConnections: true,
	showInsetsConnections: true,
	showCancelConnections: true,
	showConnectionsFlow: true,
	newProjectEnvironment: BuiltInCubeMaps.Default,
	aiCompanionPrompts: new Array<IAICompanionPrompt>(),
	aiCompanionSelectedPromptIndex: -1,
};

export const defaultProjectSettings: IProjectSettings = {
	environmentMapId: BuiltInCubeMaps.Default,
};
